// =====================================================
// S-I18N-W1a — one React owner for "which language is this page".
//
// Two runtimes have to agree and they cannot import each other:
//   • i18next owns the STRINGS (t(), namespaces, ICU plurals).
//   • locale-state.ts owns the NUMBERS AND DATES, because format.js and
//     jalali.ts are called from ~144 modules that are not React components
//     and can never receive a hook or a context.
// This provider is the single place that moves both at once, plus the
// cookie, plus `<html lang dir>`. Nothing else in the app is allowed to
// call `changeLocale` / `setLocale` directly.
// =====================================================

import React from "react";
import { useTranslation } from "react-i18next";
import { DirectionProvider } from "@radix-ui/react-direction";
import { changeLocale } from "./index";
import {
  DEFAULT_LOCALE,
  dirOf,
  getLocale,
  setLocale as setLocaleCell,
  setTranslator,
  type AppLocale,
} from "./locale-state";
import { applyDocumentLocale, applyQaPseudoDocumentLocale } from "./bootstrap";
import { qaPseudoLocaleMetadata } from "./qa-pseudo";
import { writeLocaleCookie } from "./resolve";

export interface LocaleContextValue {
  locale: AppLocale;
  dir: "rtl" | "ltr";
  /** Switch language everywhere. Resolves once i18next has the catalogues. */
  changeLocale: (next: AppLocale) => Promise<void>;
  /** True while the new namespaces are still loading (one frame, usually). */
  switching: boolean;
}

const LocaleContext = React.createContext<LocaleContextValue | null>(null);

export const LocaleProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const { t, i18n } = useTranslation();
  const [locale, setLocaleState] = React.useState<AppLocale>(() => getLocale() ?? DEFAULT_LOCALE);
  const [switching, setSwitching] = React.useState(false);
  // The override is compiled inert unless the focused browser gate opts in.
  // It exercises real AppShell/Radix geometry without exposing an incomplete
  // production locale or pretending that Persian fallback text is translated.
  const qaPseudo = React.useMemo(() => {
    if (import.meta.env.VITE_I18N_QA_PSEUDO !== "true" || typeof window === "undefined") return null;
    return qaPseudoLocaleMetadata(new URLSearchParams(window.location.search).get("__i18nQaPseudo"));
  }, []);

  React.useLayoutEffect(() => {
    if (qaPseudo) applyQaPseudoDocumentLocale(qaPseudo.languageTag);
  }, [qaPseudo]);

  // Give format.js / jalali.ts a way to reach i18next without importing it:
  // they call translate(key, persianFallback) and get the live string. The
  // fallback is the exact Persian that used to be hard-coded there, so a
  // catalogue miss degrades to today's behaviour instead of to a raw key.
  React.useEffect(() => {
    setTranslator((key, fallback, params) => {
      const hit = t(key, { defaultValue: fallback, ...params });
      return typeof hit === "string" ? hit : fallback;
    });
    return () => setTranslator(null);
  }, [t, i18n.language]);

  const change = React.useCallback(async (next: AppLocale): Promise<void> => {
    if (next === getLocale()) return;
    setSwitching(true);
    try {
      const applied = await changeLocale(next);
      setLocaleCell(applied);
      writeLocaleCookie(applied);
      applyDocumentLocale(applied);
      setLocaleState(applied);
    } finally {
      setSwitching(false);
    }
  }, []);

  const value = React.useMemo<LocaleContextValue>(
    () => ({ locale, dir: qaPseudo?.direction ?? dirOf(locale), changeLocale: change, switching }),
    [locale, change, switching, qaPseudo],
  );

  return (
    <LocaleContext.Provider value={value}>
      {/* Radix primitives do not infer <html dir>; without this provider their
          portaled menus/selects silently fall back to LTR. */}
      <DirectionProvider dir={value.dir}>{children}</DirectionProvider>
    </LocaleContext.Provider>
  );
};

/**
 * Never throws when the provider is absent — a component rendered by a test
 * harness or a Storybook story still gets today's Persian instead of a crash.
 */
export const useLocale = (): LocaleContextValue => {
  const ctx = React.useContext(LocaleContext);
  if (ctx) return ctx;
  const fallbackLocale = getLocale() ?? DEFAULT_LOCALE;
  return {
    locale: fallbackLocale,
    dir: dirOf(fallbackLocale),
    changeLocale: async () => {},
    switching: false,
  };
};
